import { CheckIcon, ChevronDownIcon, ExclamationIcon } from "@heroicons/react/outline";
import { Popover, Portal } from "@headlessui/react";
import { useRef, useState } from "react";
import { TeamRole, TeamUser } from "../interfaces";
import useUser from "../hooks/useUser";
import { useTranslation } from "react-i18next";


interface Props {
  teamUser?: TeamUser;
  role: TeamRole;
  onSelectRole: (role: TeamRole) => void;
  onRemove?: () => void;
  isUpdating?: boolean;
}

const OPTIONS: Array<TeamRole> = ["ADMIN", "EDITOR", "RUNNER", "VIEWER"];
const OPTIONS_TEXT: Record<TeamRole, string> = { "VIEWER": "Viewer", "RUNNER": "Runner", "EDITOR": "Editor", "ADMIN": "Admin", "OWNER": "Owner" };

function SettingsTeamUserRole(props: Props) {
  const { user, hasPermission } = useUser();
  const { t } = useTranslation();

  const disabled = !hasPermission(user?.activeTeam, "teams.admin") || props.role === "OWNER";

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});

  if (disabled || props.isUpdating)
    return (
      <div className="flex items-center font-semibold text-sm text-gray-600 w-full px-4 py-1 border-2 bg-gray-100">
        {props.isUpdating ? t("Updating...") : OPTIONS_TEXT[props.role]}
      </div>
    );

  function updatePosition() {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      setPanelStyle({
        position: "fixed",
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
        zIndex: 100,
      });
    }
  }

  return (
    <Popover className="relative w-full font-semibold text-sm text-gray-600">
      {({ open }) => (
        <>
          <Popover.Button ref={buttonRef} className="flex items-center font-semibold text-sm w-full py-1 px-4 border-2 border-gray-400 rounded bg-white hover:bg-blue-50" onClick={updatePosition}>
            {OPTIONS_TEXT[props.role]} <ChevronDownIcon className={`w-4 h-4 ml-auto cursor-pointer transform transition-all ${open ? "rotate-180" : "rotate-0"}`} />
          </Popover.Button>

          <Portal>
            <Popover.Panel style={panelStyle} className="flex flex-col w-40 bg-white border rounded shadow overflow-y-auto">
              {OPTIONS.map(option =>
                <Popover.Button key={option} className="flex items-center font-semibold text-sm w-full gap-1 px-2 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap" onClick={() => props.onSelectRole(option)}>
                  <CheckIcon className={`w-4 h-4 text-blue flex-shrink-0 ${props.role === option ? "visible" : "invisible"}`} />
                  {OPTIONS_TEXT[option]}
                </Popover.Button>
              )}
              {props.onRemove &&
                <Popover.Button className="flex items-center font-semibold text-sm text-gray-400 hover:text-red w-full gap-1 px-2 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap group" onClick={() => props.onRemove?.()}>
                  <ExclamationIcon className="h-4 w-4 flex-shrink-0 invisible group-hover:visible" /> {t("Remove User")}
                </Popover.Button>
              }
            </Popover.Panel>
          </Portal>
        </>
      )}
    </Popover>
  );
}

export default SettingsTeamUserRole;
